import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Message, SpinnerService } from 'shared-components';
import { EtiquetadoService } from '../../../services/etiquetado.service';
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-mantenimientoEtiquetadores-new-edit',
    templateUrl: './new-edit.component.html'
})
export class MantenimientoEtiquetadoresNewEditComponent implements OnInit, OnDestroy {

    private _etiquetador;
    public newEditForm: UntypedFormGroup;
    public showError: boolean;
    public showApiError: boolean; // evitar mostrar la clase de error en inputs si falla el getById
    public mensajes = new Array<Message>();
    public denominaciones;
    public disabled: Subject<boolean> = new Subject();
    private readonly destroy$ = new Subject<void>();

    @Input() etiquetadorId;
    @Output() refresh = new EventEmitter<boolean>();
    @Output() mensaje = new EventEmitter<string>();

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _dataProvider: DataProvider,
        private readonly _spinnerService: SpinnerService,
        private readonly _etiquetadoService: EtiquetadoService,
        private readonly _sessionContext: SessionContextService
    ) {}

    ngOnInit() {
        this.newEditForm = this._fb.group({
            pais: [''],
            denominacionId: [{ value: '', disabled: true }],
            autorizado: [''],
            reimp: [''],
            des: ['', Validators.required],
            alias: [''],
            nifcif: [''],
            domicilioFiscal: ['', Validators.required],
            domicilioFiscalAlias: [''],
            etqAutorizado: [''],
            etqReimp: [''],
        });

        if (this.etiquetadorId) {
            this.getEtiquetador();
        }

        this.newEditForm.get('pais').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pais) => {
            if (pais) {
                let _filters = {
                    paisId: pais.id
                }
                this._spinnerService.showSpinner();
                this._etiquetadoService.getDenominacionesPais(_filters).toPromise().finally(() => {
                    this._spinnerService.hideSpinner();
                }).then((resp) => {
                    if (resp.lista.length > 0) {
                        this.denominaciones = resp.lista;
                        this.newEditForm.get('denominacionId').enable();
                        this.disabled.next(false);
                    } else if (resp.lista.length === 0) {
                        this.newEditForm.get('denominacionId').enable();
                        this.newEditForm.get('denominacionId').patchValue('');
                        this.disabled.next(true);
                    }
                });
            } else {
                this.denominaciones = [];
                this.newEditForm.get('denominacionId').disable();
                this.newEditForm.get('denominacionId').patchValue('');
                this.disabled.next(true);
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get paises() {
        if (this._dataProvider.paises) {
            if (this._dataProvider.paises.length === 1 && this.newEditForm.get('pais').enabled) {
              this.newEditForm.get('pais').patchValue(this._dataProvider.paises[0]);
              this.newEditForm.get('pais').disable();
            }
          }
        return this._dataProvider.paises;
    }

    public get isSmallScreen() {
        return this._sessionContext.isSmallScreen();
    }

    public getEtiquetador() {
        this._spinnerService.showSpinner();
        this._etiquetadoService.getEtiquetador(this.etiquetadorId).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this._etiquetador = resp;
            this.newEditForm.patchValue({
                des: resp.des,
                alias: resp.alias,
                nifcif: resp.nifcif,
                domicilioFiscal: resp.domicilioFiscal,
                domicilioFiscalAlias: resp.domicilioFiscalAlias,
                etqAutorizado: resp.impresionAutorizada,
                etqReimp: resp.reimpresionAutorizada
            });
        }, (error) => {
            this.showApiError = true;
            if (error.status === 420) {
                this.mensajes.push({ class: 'error', message: 'global.error.noAccess' });
            } else {
                this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
            }
        });
    }

    public close() {
        this.refresh.emit(false);
    }

    public save() {
        this.checkForm();
        if (this.newEditForm.valid && !this.etiquetadorId) {
            this._etiquetador = {
                usuarioEtiquetador: {
                    etiquetador: {
                        des: this.newEditForm.get('des').value,
                        alias: this.newEditForm.get('alias').value,
                        nifcif: this.newEditForm.get('nifcif').value,
                        impresionAutorizada: this.newEditForm.get('autorizado').value ? this.newEditForm.get('autorizado').value : false,
                        reimpresionAutorizada: this.newEditForm.get('reimp').value ? this.newEditForm.get('reimp').value : false,
                        domicilioFiscal: this.newEditForm.get('domicilioFiscal').value,
                        domicilioFiscalAlias: this.newEditForm.get('domicilioFiscalAlias').value
                    },
                    etiquetadorPaisDenominacion: {
                        paisId: this.newEditForm.get('pais').value.id,
                        denominacionId: this.newEditForm.get('denominacionId').value.id,
                        impresionAutorizada: this.newEditForm.get('autorizado').value ? this.newEditForm.get('autorizado').value : false ,
                        reimpresionAutorizada: this.newEditForm.get('reimp').value ? this.newEditForm.get('reimp').value : false,
                    }
                }
            };
            this._spinnerService.showSpinner();
            this._etiquetadoService.createEtiquetador(this._etiquetador).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
                if (resp) {
                  this._dataProvider.refreshEmpacadores();
                  this.refresh.emit(true);
                  this.mensaje.emit('global.success.itemCreated');
                } else {
                  this.showError = true;
                  this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
                }
            },
            (error) => {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
            });
        } else if (this.newEditForm.valid && this.etiquetadorId) {
            this._etiquetador = {
                id: this.etiquetadorId,
                des: this.newEditForm.get('des').value,
                alias: this.newEditForm.get('alias').value,
                nifcif: this.newEditForm.get('nifcif').value,
                impresionAutorizada: this.newEditForm.get('etqAutorizado').value ? this.newEditForm.get('etqAutorizado').value : false,
                reimpresionAutorizada: this.newEditForm.get('etqReimp').value ? this.newEditForm.get('etqReimp').value : false,
                domicilioFiscal: this.newEditForm.get('domicilioFiscal').value,
                domicilioFiscalAlias: this.newEditForm.get('domicilioFiscalAlias').value
            };
            this._spinnerService.showSpinner();
            this._etiquetadoService.updateEtiquetador(this._etiquetador).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
                this._dataProvider.refreshEmpacadores();
                this.refresh.emit(true);
                this.mensaje.emit('global.success.itemCreated');
            },
            (error) => {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
            });
        }
    }

    private checkForm() {
        this.showError = this.newEditForm.invalid;
        if (this.showError) {
          this.mensajes.push({ class: 'error', message: 'global.error.required' });
        }
      }
}